/**
 * EditSection Component
 *
 * Renders a form section based on page layout configuration.
 * Used in edit modals to ensure consistency with view mode.
 */

import React from 'react';
import { useSectionFields } from '../../hooks/useObjectManager';
import { EditField } from './EditField';
import type { LayoutSection } from '../../services/mockData/objectManagerLayouts';

interface EditSectionProps {
  section: LayoutSection;
  objectApiName: string;
  formData: Record<string, any>;
  onChange: (fieldName: string, value: any) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
}

export const EditSection: React.FC<EditSectionProps> = ({
  section,
  objectApiName,
  formData,
  onChange,
  errors = {},
  disabled = false
}) => {
  const fields = useSectionFields(objectApiName, section.fields || []);

  if (fields.length === 0) return null;

  return (
    <div className="mb-6">
      <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        {section.title}
        <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
          ({fields.length} field{fields.length !== 1 ? 's' : ''})
        </span>
      </h3>

      <div className={`grid gap-4 ${section.columns === 2 ? 'md:grid-cols-2' : 'grid-cols-1'}`}>
        {fields.map(field => {
          if (!field) return null;

          return (
            <EditField
              key={field.fieldName}
              field={field}
              value={formData[field.fieldName]}
              onChange={(value) => onChange(field.fieldName, value)}
              error={errors[field.fieldName]}
              disabled={disabled}
            />
          );
        })}
      </div>
    </div>
  );
};
